/**
 * The merchant sign-up password policy, and how strong a password reads.
 *
 * Mirrors `validate_merchant_password` in `app/schemas/auth.py`. The server is
 * the authority; this exists so the person typing sees what is missing before
 * they submit, not after a 422.
 */

export const MIN_PASSWORD_LENGTH = 10;

/** Length at which a password that meets every rule counts as very strong. */
const VERY_STRONG_LENGTH = 14;

export interface PasswordRule {
  id: 'length' | 'upper' | 'lower' | 'number' | 'special';
  label: string;
  test: (value: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'length',
    label: `${MIN_PASSWORD_LENGTH}+ characters`,
    test: (value) => value.length >= MIN_PASSWORD_LENGTH,
  },
  { id: 'upper', label: 'Uppercase', test: (value) => /[A-Z]/.test(value) },
  { id: 'lower', label: 'Lowercase', test: (value) => /[a-z]/.test(value) },
  { id: 'number', label: 'Number', test: (value) => /[0-9]/.test(value) },
  { id: 'special', label: 'Special character', test: (value) => /[^A-Za-z0-9]/.test(value) },
];

export type StrengthLevel = 'weak' | 'medium' | 'strong' | 'very_strong';

export interface PasswordStrength {
  /** 0 for an empty box, then 1 (weak) to 4 (very strong). */
  score: 0 | 1 | 2 | 3 | 4;
  level: StrengthLevel | null;
  label: string | null;
  met: Record<PasswordRule['id'], boolean>;
  /** Every rule is satisfied, so the server will accept the composition. */
  meetsPolicy: boolean;
}

const LABELS: Record<StrengthLevel, string> = {
  weak: 'Weak',
  medium: 'Medium',
  strong: 'Strong',
  very_strong: 'Very strong',
};

/**
 * Weak until at most one rule is missing, medium with exactly one missing,
 * strong once every rule is met, and very strong when it is also long and
 * varied - length is what actually makes a password hard to guess.
 */
export function passwordStrength(value: string): PasswordStrength {
  const met = Object.fromEntries(
    PASSWORD_RULES.map((rule) => [rule.id, rule.test(value)]),
  ) as PasswordStrength['met'];
  const metCount = Object.values(met).filter(Boolean).length;
  const meetsPolicy = metCount === PASSWORD_RULES.length;

  if (!value) return { score: 0, level: null, label: null, met, meetsPolicy: false };

  let level: StrengthLevel;
  if (meetsPolicy) {
    level =
      value.length >= VERY_STRONG_LENGTH && new Set(value).size >= 8 ? 'very_strong' : 'strong';
  } else {
    level = metCount === PASSWORD_RULES.length - 1 ? 'medium' : 'weak';
  }

  const score = ({ weak: 1, medium: 2, strong: 3, very_strong: 4 } as const)[level];
  return { score, level, label: LABELS[level], met, meetsPolicy };
}
